import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { useSignalRContext } from '../components/Contexts/SignalRContext'
import { convertDatesToUTC } from '../ajax/controller/AjaxController'
import { offlineDb, type MutationQueueItem } from './db'
import { beginSync, endSync, reportSynced } from './syncStatus'
import { store } from '../state/store'
import { clearProvisionalShift } from '../state/reducers/shiftReducer'
import { refreshLatestShift } from './refreshLatestShift'
import { patchCachedEntityId, patchCachedRowVersion } from './patchCachedRowVersion'
import { syncAjax } from '../ajax/SyncAjax'
import { SyncOperation, SyncStatus } from '../DTOs/Sync/SyncDtos'
import type { SyncRequest, SyncResponse } from '../DTOs/Sync/SyncDtos'
import type { ActionResponse } from '../DTOs/Common/ErrorResponse'
import { recordSyncFailure, uploadSyncFailures } from './syncFailures'
import { isOnlineNow, subscribeToReachability } from './connectivity'

function operationCode(op: MutationQueueItem['operation']) {
  if (op === 'create') return SyncOperation.Create
  if (op === 'update') return SyncOperation.Update
  return SyncOperation.Delete
}

// Note: a 401 says the session could not be renewed, not that the change was wrong. The queue survives
// sign-out, so leaving the change queued lets it sync once the user signs back in.
function isSessionFailure(code?: number) {
  return code === 401
}

const SERVER_ERROR_RETRY_MS = 7 * 24 * 60 * 60 * 1000

// Note: 0 is no reply at all, the network gone or the request timed out. It goes through keep like any other
// failure, so an outage is logged once per change instead of showing only as a queue that will not drain.
const NO_REPLY = 0
const NO_REPLY_MESSAGE = 'No reply from the server: the network dropped or the request timed out.'

function isServerError(code?: number) {
  return code !== undefined && code >= 500
}

type SyncAttemptMode = 'batch' | 'single'

function buildSyncRequest(items: MutationQueueItem[]): SyncRequest {
  const request: SyncRequest = { stoppages: [], loadings: [], diggingPatterns: [], patternBlasts: [], completeShifts: [], geoRehandles: [], operationLocations: [], engineHours: [] }
  for (const item of items) {
    const base = {
      clientId: item.clientId,
      operation: operationCode(item.operation),
      shiftId: item.shiftId,
      rowVersion: item.rowVersion,
      data: convertDatesToUTC(item.data),
    }
    if (item.entity === 'stoppage') {
      const { plant: _p, stoppageType: _st, stoppageSubType: _sst, location: _l, ...safeData } = (base.data ?? {}) as any
      request.stoppages.push({ ...base, data: safeData, stoppageId: item.entityId })
    } else if (item.entity === 'loading') {
      request.loadings.push({ ...base, loadingId: item.entityId })
    } else if (item.entity === 'diggingPattern') {
      request.diggingPatterns.push({ ...base, diggingPatternId: item.entityId })
    } else if (item.entity === 'patternBlast') {
      request.patternBlasts.push({ ...base })
    } else if (item.entity === 'completeShift') {
      request.completeShifts.push({ clientId: item.clientId, shiftId: item.shiftId })
    } else if (item.entity === 'operationLocation') {
      const d = (item.data ?? {}) as { operationId?: number; locationId?: number; operationPlantId?: number }
      request.operationLocations.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        locationId: d.locationId ?? 0,
        shiftId: item.shiftId,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'engineHours') {
      const d = (item.data ?? {}) as { operationId?: number; startEngineHours?: number; endEngineHours?: number; fuel?: number | null; operationPlantId?: number }
      request.engineHours.push({
        clientId: item.clientId,
        operationId: d.operationId ?? item.entityId ?? 0,
        shiftId: item.shiftId,
        startEngineHours: d.startEngineHours,
        endEngineHours: d.endEngineHours,
        fuel: d.fuel ?? undefined,
        operationPlantId: d.operationPlantId,
      })
    } else if (item.entity === 'geoRehandle') {
      request.geoRehandles.push({ clientId: item.clientId, operation: operationCode(item.operation), geoRehandleId: item.entityId, rowVersion: item.rowVersion, shiftId: item.shiftId, data: item.data })
    }
  }
  return request
}

// Note: a change to a row whose create is still queued waits for that create to sync and swap its temp id for
// the real one. Sent in the same request it would still carry the temp id, which the server cannot find.
function waitsForCreate(item: MutationQueueItem, queue: MutationQueueItem[]): boolean {
  return item.operation !== 'create'
    && typeof item.entityId === 'number'
    && item.entityId < 0
    && queue.some(other => other.operation === 'create' && other.entity === item.entity && other.entityId === item.entityId)
}

async function waitsForPattern(item: MutationQueueItem): Promise<boolean> {
  const patternId = (item.data as { diggingPatternId?: number } | undefined)?.diggingPatternId
  if (item.entity !== 'loading' || typeof patternId !== 'number' || patternId >= 0) return false
  return await offlineDb.mutationQueue.where('[entity+entityId]').equals(['diggingPattern', patternId]).count() > 0
}

async function remapQueuedPatternId(tempId: number, realId: number): Promise<void> {
  const queued = await offlineDb.mutationQueue.where('entity').equals('loading').toArray()

  for (const item of queued) {
    const data = item.data as { diggingPatternId?: number } | undefined
    if (!data || data.diggingPatternId !== tempId) continue
    await offlineDb.mutationQueue.update(item.id!, { data: { ...data, diggingPatternId: realId } })
  }
}

async function remapQueuedEntityId(
  entity: MutationQueueItem['entity'],
  tempId: number,
  realId: number,
): Promise<void> {
  const queued = await offlineDb.mutationQueue
    .where('[entity+entityId]')
    .equals([entity, tempId])
    .toArray()

  for (const item of queued) {
    const data = item.data && typeof item.data === 'object'
      ? { ...(item.data as Record<string, unknown>) }
      : item.data

    if (data && typeof data === 'object') {
      for (const idField of ['loadingId', 'stoppageId', 'geoRehandleId', 'diggingPatternId']) {
        if ((data as Record<string, unknown>)[idField] === tempId) {
          (data as Record<string, unknown>)[idField] = realId
        }
      }
    }

    await offlineDb.mutationQueue.update(item.id!, { entityId: realId, data })
  }
}

async function refreshStaleQueries(queryClient: QueryClient, staleEntities: Set<MutationQueueItem['entity']>) {
  try {
    for (const entity of staleEntities) {
      if (entity === 'stoppage') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'stoppages'] })
      } else if (entity === 'loading') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'truckLoads'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
      } else if (entity === 'diggingPattern') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggingLoadingSummary'] })
      } else if (entity === 'operationLocation' || entity === 'engineHours') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
        await queryClient.invalidateQueries({ queryKey: ['operations'] })
      } else if (entity === 'geoRehandle') {
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandles'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'crushing'] })
      } else if (entity === 'completeShift') {
        store.dispatch(clearProvisionalShift())
        await refreshLatestShift()
        await queryClient.invalidateQueries({ queryKey: ['dataTable'] })
        await queryClient.invalidateQueries({ queryKey: ['chatree'] })
        await queryClient.invalidateQueries({ queryKey: ['production'] })
        await queryClient.invalidateQueries({ queryKey: ['geology'] })
        await queryClient.invalidateQueries({ queryKey: ['shiftExistence'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'operations'] })
        await queryClient.invalidateQueries({ queryKey: ['operations'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'production'] })
        await queryClient.invalidateQueries({ queryKey: ['production'] })
      } else {
        await queryClient.invalidateQueries({ queryKey: ['references', 'blastmasterIds'] })
        await queryClient.invalidateQueries({ queryKey: ['dataTable', 'chatree', 'diggerLocations'] })
      }
    }
  } catch (error) {
    console.error('Refreshing after a sync failed', error)
  }
}

export function useSyncWorker() {
  const queryClient = useQueryClient()
  const { connection } = useSignalRContext()
  const pendingCount = useLiveQuery(() => offlineDb.mutationQueue.count(), []) ?? 0
  const isSyncingRef = useRef(false)

  const runSync = useCallback(async () => {
    if (isSyncingRef.current) return
    isSyncingRef.current = true
    try {
      const queue = await offlineDb.mutationQueue.orderBy('createdAt').toArray()
      const snapshot = queue.filter(item => !waitsForCreate(item, queue))

      if (snapshot.length) beginSync(snapshot.length)

      if (navigator.onLine
        && store.getState().shift.provisionalShiftDate
        && !queue.some(item => item.entity === 'completeShift')) {
        store.dispatch(clearProvisionalShift())
      }

      if (snapshot.length === 0) return

      const staleEntities = new Set<MutationQueueItem['entity']>()

      const processResults = async (results: SyncResponse['results'], items: MutationQueueItem[]) => {
        reportSynced(results.length)
        const byClientId = new Map(items.map(i => [i.clientId, i]))
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          for (const result of results) {
            const item = byClientId.get(result.clientId)
            if (!item) continue
            staleEntities.add(item.entity)

            if (item.operation === 'create'
              && result.status === SyncStatus.Applied
              && typeof result.serverId === 'number'
              && typeof item.entityId === 'number'
              && item.entityId < 0) {
              if (item.entity === 'diggingPattern') {
                await remapQueuedPatternId(item.entityId, result.serverId)
              }
              await remapQueuedEntityId(item.entity, item.entityId, result.serverId)
              patchCachedEntityId(item.entity, item.entityId, result.serverId)
            }

            if (item.operation === 'update' && result.status === SyncStatus.Applied) {
              patchCachedRowVersion(item.entity, item.entityId, result.serverRowVersion)
            }
            if (result.status !== SyncStatus.Applied) {
              await offlineDb.syncIssues.add({
                clientId: result.clientId,
                entity: item.entity,
                operation: item.operation,
                status: result.status,
                entityId: item.entityId,
                shiftId: item.shiftId,
                serverState: result.serverState,
                clientData: item.data,
                createdAt: Date.now(),
              })
            }
            await offlineDb.mutationQueue.where('clientId').equals(result.clientId).delete()
          }
        })
      }

      const quarantine = async (item: MutationQueueItem, code?: number, reason?: string) => {
        staleEntities.add(item.entity)
        await offlineDb.transaction('rw', offlineDb.mutationQueue, offlineDb.syncIssues, async () => {
          await offlineDb.syncIssues.add({
            clientId: item.clientId,
            entity: item.entity,
            operation: item.operation,
            status: SyncStatus.Error,
            entityId: item.entityId,
            shiftId: item.shiftId,
            serverState: reason ?? `The server rejected this change${code ? ` (HTTP ${code})` : ''}, so it could not be applied. It has been removed from the queue - please re-enter it.`,
            clientData: item.data,
            createdAt: Date.now(),
          })
          await offlineDb.mutationQueue.where('clientId').equals(item.clientId).delete()
        })
      }

      // Note: a kept change is retried every 15 seconds, so it is logged when its failure changes,
      // not on every retry.
      const keep = async (item: MutationQueueItem, code: number | undefined, message: string | undefined, mode: SyncAttemptMode) => {
        const serverErrorSince = isServerError(code) ? item.serverErrorSince ?? Date.now() : undefined
        if (item.lastFailureCode === code && item.serverErrorSince === serverErrorSince) return
        await offlineDb.mutationQueue.update(item.id!, { lastFailureCode: code, serverErrorSince })
        await recordSyncFailure({ ...item, serverErrorSince }, code, message, 'Kept', { mode, batchSize: snapshot.length })
      }

      const handleFailure = async (item: MutationQueueItem, errors: ActionResponse<unknown>['errors'], mode: SyncAttemptMode) => {
        const code = errors?.[0]?.code
        const message = errors?.[0]?.description
        if (isSessionFailure(code)) {
          await keep(item, code, message, mode)
          return 'stop'
        }
        if (isServerError(code) && Date.now() - (item.serverErrorSince ?? Date.now()) < SERVER_ERROR_RETRY_MS) {
          await keep(item, code, message, mode)
          return 'continue'
        }
        const reason = isServerError(code)
          ? `The server kept failing (HTTP ${code}) for 7 days, so this change could not be applied. It has been removed from the queue - please re-enter it.`
          : undefined
        await quarantine(item, code, reason)
        await recordSyncFailure(item, code, message, 'Removed', { mode, batchSize: snapshot.length })
        return 'continue'
      }

      try {
        const response = await syncAjax.sync(buildSyncRequest(snapshot)).catch(() => null)
        if (response === null) {
          for (const item of snapshot) await keep(item, NO_REPLY, NO_REPLY_MESSAGE, 'batch')
          return
        }
        if (response.success && response.data) {
          await processResults(response.data.results, snapshot)
        } else if (isSessionFailure(response.errors?.[0]?.code)) {
          for (const item of snapshot) await keep(item, 401, response.errors?.[0]?.description, 'batch')
          return
        } else if (snapshot.length === 1) {
          await handleFailure(snapshot[0], response.errors, 'batch')
        } else {
          for (const item of snapshot) {
            const current = await offlineDb.mutationQueue.get(item.id!)
            if (!current || await waitsForPattern(current)) continue
            const single = await syncAjax.sync(buildSyncRequest([current])).catch(() => null)
            if (single === null) {
              await keep(current, NO_REPLY, NO_REPLY_MESSAGE, 'single')
              return
            }
            if (single.success && single.data) {
              await processResults(single.data.results, [current])
            } else if (await handleFailure(current, single.errors, 'single') === 'stop') {
              return
            }
          }
        }
      } catch {
        return
      }

      // Note: not awaited, so the lock is released first. These refetches retry, and pause while the screen is
      // hidden, so awaiting them held the lock long after the changes themselves had synced.
      void refreshStaleQueries(queryClient, staleEntities)
    } finally {
      isSyncingRef.current = false
      endSync()
      void uploadSyncFailures()
    }
  }, [queryClient])

  useEffect(() => {
    let active = true
    const trigger = () => { if (active && isOnlineNow()) runSync() }

    trigger()
    const unsubscribe = subscribeToReachability(trigger)
    connection?.onreconnected(() => { if (active) runSync() })

    const retry = pendingCount > 0 ? setInterval(trigger, 15000) : null

    return () => {
      active = false
      unsubscribe()
      if (retry) clearInterval(retry)
    }
  }, [connection, runSync, pendingCount])
}
